import { createContext, useContext, useEffect, useReducer } from 'react';
import { getLinkAnalytics, getProfileActivity } from '../services/analyticsService';
import { aggregateByLink, buildDailyData } from '../utils';

const INITIAL_SLICE = { data: null, loading: true, error: null };

const initialState = {
  links: INITIAL_SLICE,
  activity: INITIAL_SLICE,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'LINKS_SUCCESS':
      return { ...state, links: { data: action.payload, loading: false, error: null } };
    case 'LINKS_ERROR':
      return { ...state, links: { data: null, loading: false, error: action.payload } };
    case 'ACTIVITY_SUCCESS':
      return { ...state, activity: { data: action.payload, loading: false, error: null } };
    case 'ACTIVITY_ERROR':
      return { ...state, activity: { data: null, loading: false, error: action.payload } };
    default:
      return state;
  }
};

const AnalyticsContext = createContext();

export function AnalyticsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([getLinkAnalytics(), getProfileActivity()]).then(([linksResult, activityResult]) => {
      if (cancelled) return;

      if (linksResult.status === 'fulfilled') {
        dispatch({ type: 'LINKS_SUCCESS', payload: aggregateByLink(linksResult.value.data.analytics) });
      } else {
        dispatch({ type: 'LINKS_ERROR', payload: linksResult.reason });
      }
      if (activityResult.status === 'fulfilled') {
        const visits = activityResult.value.data.analytics;
        const totalVisits = visits.reduce((sum, v) => sum + v.visits, 0);
        const totalClicks = linksResult.status === 'fulfilled'
          ? linksResult.value.data.analytics.reduce((sum, l) => sum + l.clicks, 0)
          : 0;
        dispatch({
          type: 'ACTIVITY_SUCCESS',
          payload: { dailyData: buildDailyData(visits, 7), totalVisits, totalClicks },
        });
      } else {
        dispatch({ type: 'ACTIVITY_ERROR', payload: activityResult.reason });
      }
    });

    return () => { cancelled = true; };
  }, []);

  return (
    <AnalyticsContext.Provider value={state}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export const useAnalytics = () => useContext(AnalyticsContext);
